import {
  type LeadBucket,
  type LeadListItem,
  type LeadListQuery,
  type LeadPriority,
} from '@utazzo/shared';
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { CARRYFORWARD_FLOOR_ISO, getPrevMonthEndIso } from '../carryforward';
import { ExitUnassignedAssignDialog } from '../components/ExitUnassignedAssignDialog';
import { LeadBulkReassignDialog } from '../components/LeadBulkReassignDialog';
import {
  isoDate,
  type LeadDateRangePresetKey,
  parsePresetKey,
  resolveLeadDateRange,
} from '../lead-date-range';
import { useLeadListColumns } from '../lead-list-columns';
import { listLeads } from '../leads.api';
import {
  useAgentOptionsForLeads,
  useDestinationOptionsForLeads,
  useQueryStatusOptionsWithBucket,
} from '../use-lead-options';
import { useExitUnassignedFormerOwners, useLeadsList } from '../use-leads';

import { type DataGridFilter } from '@/components/data-grid';
import { ListScreen } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const MIN_SEARCH_LENGTH = 3;

const PRIORITY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Any intent' },
  { value: 'URGENT', label: 'Urgent' },
  { value: 'HIGH', label: 'High' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'LOW', label: 'Low' },
];

const DUE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Any follow-up' },
  { value: 'due', label: 'Follow-up due' },
];

const CARRYFORWARD_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Both leads age' },
  // 'yes' = ONLY the carry-forward slice (OPEN leads created before this
  // month, floored at the cutover anchor) - it replaces the date window
  // rather than adding to it, so "Only" is what the operator actually gets.
  { value: 'yes', label: 'Only carry forward' },
  // 'no' = exclude carry-forward leads: keep the date-window slice but
  // suppress the older still-active leads that "Any lead age" folds in
  // via includeActiveCarryover. Useful when the operator wants only the
  // leads that originate inside the selected window.
  { value: 'no', label: 'Without carry forward' },
];

/// Budget brackets. The value is encoded as `from-to` (either bound may
/// be empty for unbounded). `toQuery` parses these into the numeric
/// `budgetFrom` / `budgetTo` server params. Brackets line up with the
/// legacy agent workflow (₹50K / ₹1L / ₹3L / ₹5L / ₹10L grade ladder).
const BUDGET_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Any budget' },
  { value: '0-50000', label: 'Under ₹50K' },
  { value: '50000-100000', label: '₹50K - ₹1L' },
  { value: '100000-300000', label: '₹1L - ₹3L' },
  { value: '300000-500000', label: '₹3L - ₹5L' },
  { value: '500000-1000000', label: '₹5L - ₹10L' },
  { value: '1000000-', label: 'Above ₹10L' },
];

/// Parse a `from-to` budget bracket value into raw numeric bounds.
/// Returns `null` for an empty / malformed value. `to` may be undefined
/// for the open-ended "Above" bracket.
function parseBudgetBracket(value: string | undefined): { from?: number; to?: number } | null {
  if (!value) return null;
  const parts = value.split('-');
  if (parts.length !== 2) return null;
  const from = parts[0] ? Number(parts[0]) : undefined;
  const to = parts[1] ? Number(parts[1]) : undefined;
  if (from !== undefined && !Number.isFinite(from)) return null;
  if (to !== undefined && !Number.isFinite(to)) return null;
  return { from, to };
}

/// "Created month" filter. Value is `YYYY-MM`, most recent first. 2026-08-01:
/// only offers the months the grid can actually contain - from the carry-forward
/// floor (the oldest created date any pool row can have) up to the current month
/// - instead of a static 15-month list padded with empty months. The grid is a
/// this-month + carry-forward snapshot, so those are the only created months
/// present (a lead created before the floor only survives as a closed-this-period
/// row, a rare edge; it just won't have its own filter option).
const CREATED_MONTH_OPTIONS: ReadonlyArray<{ value: string; label: string }> = (() => {
  const floorYear = Number(CARRYFORWARD_FLOOR_ISO.slice(0, 4));
  const floorMonth = Number(CARRYFORWARD_FLOOR_ISO.slice(5, 7));
  const floor = new Date(floorYear, floorMonth - 1, 1);
  const now = new Date();
  const opts: Array<{ value: string; label: string }> = [{ value: '', label: 'Any created month' }];
  for (
    let d = new Date(now.getFullYear(), now.getMonth(), 1);
    d >= floor;
    d.setMonth(d.getMonth() - 1)
  ) {
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    opts.push({ value, label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) });
  }
  return opts;
})();

/// `YYYY-MM` -> inclusive createdAt day bounds (YYYY-MM-DD) for that month.
function monthBounds(ym: string | undefined): { from: string; to: string } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(ym ?? '');
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const lastDay = new Date(year, month, 0).getDate();
  return { from: `${ym}-01`, to: `${ym}-${String(lastDay).padStart(2, '0')}` };
}

// The "Created" date range is owned by the inbox header (DateRangeFilter)
// so it can drive both the Pipeline tab tiles and this grid in lockstep.
// Don't add a Created filter here - it would compete with the URL params.
// `FILTERS_STATIC` is the order-fixed sub-list; the Status filter is built
// inside the component because its options come from a live API hook.
//
// We deliberately drop the Bucket filter: every Stage is bucketed, so the
// per-Stage dropdown already covers OPEN/WON/LOST without forcing a second
// click. Tile drill-ins still set `currentBucket` via URL params; this just
// removes it from the visible filter row.
// Sentinel value for the "Assigned To" filter's "Unassigned" option. Not a real
// agent UUID, so it can't collide with one; toQuery translates it to the
// `assignedAgentIsNull` server flag.
const UNASSIGNED_ASSIGNEE_FILTER = 'unassigned';

const FILTERS_STATIC: ReadonlyArray<DataGridFilter> = [
  {
    id: 'createdMonth',
    label: 'Created month',
    options: CREATED_MONTH_OPTIONS,
    widthClass: 'w-44',
  },
  { id: 'priority', label: 'Lead Intent', options: PRIORITY_OPTIONS, widthClass: 'w-40' },
  { id: 'due', label: 'Follow-up', options: DUE_OPTIONS, widthClass: 'w-44' },
  {
    id: 'carryforward',
    label: 'Carry forward',
    options: CARRYFORWARD_OPTIONS,
    widthClass: 'w-44',
  },
];

/**
 * Columns for the Excel export. Mirrors the visible grid plus a few
 * machine-friendly fields (reference, ad attribution, dates as YYYY-MM-DD)
 * so an export round-trips cleanly and matches the import template's shape.
 * The `export` config on the grid pages through EVERY matching row for the
 * current filters (not just the visible page) via `fetchAllPages`.
 */
const leadExportColumns: ReadonlyArray<{ header: string; value: (r: LeadListItem) => unknown }> = [
  { header: 'Reference', value: (r) => r.reference },
  { header: 'Customer Name', value: (r) => r.customer.displayName },
  { header: 'Mobile', value: (r) => r.customer.mobileNumber ?? '' },
  { header: 'Email', value: (r) => r.customer.email ?? '' },
  { header: 'Stage', value: (r) => r.currentStatus.name },
  { header: 'Sub-stage', value: (r) => r.currentSubStatus?.name ?? '' },
  { header: 'Lost reason', value: (r) => r.lostReason?.name ?? '' },
  { header: 'Latest remark', value: (r) => r.lastFollowUpNote ?? '' },
  { header: 'Bucket', value: (r) => r.currentBucket },
  { header: 'Lead Intent', value: (r) => r.priority },
  { header: 'Category', value: (r) => r.leadCategory ?? '' },
  { header: 'Owner', value: (r) => r.assignedAgent?.fullName ?? '' },
  { header: 'Departure', value: (r) => r.departureCity?.name ?? '' },
  {
    header: 'Destination',
    value: (r) => r.destinationCity?.name ?? r.destination?.name ?? r.destinationCityText ?? '',
  },
  { header: 'Budget', value: (r) => r.budgetAmount ?? '' },
  { header: 'Currency', value: (r) => r.budgetCurrency ?? '' },
  { header: 'Adults', value: (r) => r.travellerAdults },
  { header: 'Children', value: (r) => r.travellerChildren },
  { header: 'Infants', value: (r) => r.travellerInfants },
  { header: 'Expected Travel Start', value: (r) => r.expectedTravelStart ?? '' },
  { header: 'Expected Travel End', value: (r) => r.expectedTravelEnd ?? '' },
  {
    header: 'Next Follow-up',
    value: (r) => (r.nextFollowUpAt ? r.nextFollowUpAt.slice(0, 10) : ''),
  },
  { header: 'Created On', value: (r) => r.createdAt.slice(0, 10) },
  { header: 'Ad ID', value: (r) => r.adId ?? '' },
  { header: 'Ad Name', value: (r) => r.adName ?? '' },
  { header: 'Adset ID', value: (r) => r.adsetId ?? '' },
  { header: 'Adset Name', value: (r) => r.adsetName ?? '' },
  { header: 'Campaign ID', value: (r) => r.campaignId ?? '' },
  { header: 'Campaign Name', value: (r) => r.campaignName ?? '' },
];

interface LeadsListPageProps {
  /**
   * Render only the grid (without the page header). Used when the page is
   * embedded inside the Lead inbox tabs - the host renders the title +
   * actions itself. See ADR-0032 §2.
   */
  embedded?: boolean;
  /**
   * QueryStatus.code to hard-lock the stage filter to. Used by the
   * "Long Follow-up" tab which embeds this page pre-filtered to
   * `long_followup`. When set:
   *   - the matching status id is auto-applied to every list query
   *   - the Stage chip is dropped from the filter bar (the operator
   *     can't dodge the lock from inside this tab)
   *   - the URL `?stage=...` param is ignored for the same reason
   * Server-side RBAC scoping still applies on top - this prop narrows
   * within the operator's visible set, never broadens it.
   */
  forcedStatusCode?: string;
  /**
   * QueryStatus.code to silently exclude from results. Used by the
   * Pipeline tab to hide `long_followup` leads (they live on the
   * dedicated tab). The matching status is also dropped from the
   * Stage filter dropdown so the operator can't pick a value that
   * would always return zero rows.
   */
  excludeStatusCode?: string;
  /**
   * Opt into the pipeline carry-forward semantic: when on, the
   * server folds still-OPEN + still-`active` leads from before the
   * createdAt window into the result set, so an active lead doesn't
   * fall off the operator's screen as the calendar advances. See
   * `LeadListQuerySchema.includeActiveCarryover` for the predicate.
   * Auto-suppressed when the operator's current filter chips would
   * make the carryover leg semantically incompatible (explicit non-
   * OPEN bucket, a specific non-active stage, a tile-drill-in like
   * Revert Pending / Today's Follow-up, or `daterange=all`).
   */
  enableActiveCarryover?: boolean;
  /**
   * 2026-07-08. Owner scope from the inbox's role-hierarchy filter.
   * When set, every /leads request piggybacks `assignedAgentId=<ownerId>`
   * so the grid stays in sync with the picked owner regardless of what
   * the internal DataGrid chip is doing. The chip still works for
   * inline overrides; this prop is the outer scope.
   */
  ownerId?: string | null;
  /**
   * 2026-08-01. Hard-coded date scope, replacing the removed URL date filter.
   * When set, it overrides the (now empty) `?daterange` param - e.g. the Long
   * Follow-up tab passes `"all"` (parking lot is timeless). Omitted => the grid
   * defaults to this-month + carry-forward like the rest of the inbox snapshot.
   */
  rangePreset?: LeadDateRangePresetKey;
}

export const LeadsListPage = ({
  embedded,
  forcedStatusCode,
  excludeStatusCode,
  enableActiveCarryover,
  ownerId,
  rangePreset,
}: LeadsListPageProps = {}): React.JSX.Element => {
  const navigate = useNavigate();
  // Date range lives in URL params (`?daterange=...&from=...&to=...`) - the
  // inbox header writes them and we read them here so the grid stays in
  // sync with the Pipeline tab. `daterange` (not `range`) to avoid colliding
  // with the LeadInboxAside's `range` param on the detail screen.
  const [searchParams] = useSearchParams();
  const urlExitUnassigned = searchParams.get('exitUnassigned') === 'true';
  // Effective date scope: the `rangePreset` prop (hard-coded per tab) wins;
  // otherwise fall back to the URL / this-month default. Date params are no
  // longer written to the URL (removed 2026-08-01), so in practice this is the
  // prop for Long Follow-up ('all') and this-month everywhere else. Named
  // presets are relative windows resolved at read time (lead-date-range.ts).
  const effectivePreset = rangePreset ?? parsePresetKey(searchParams.get('daterange'));
  const presetRange = resolveLeadDateRange(
    effectivePreset,
    searchParams.get('from'),
    searchParams.get('to'),
  );
  const createdAtFrom = searchParams.get('from') ?? isoDate(presetRange.from);
  const createdAtTo = searchParams.get('to') ?? isoDate(presetRange.to);
  // `all` means "no date filter at all" - even if from/to linger, drop them.
  const includeDateRange = effectivePreset !== 'all';

  // Tile drill-ins pass filter params in the URL; we read them here so the
  // rows match whichever tile drove the navigation. The tracker strip
  // (ADR-0032 §20) and the pipeline tiles share these URL params, so
  // either entry point lands on the same filtered grid.
  const urlBucketRaw = searchParams.get('bucket');
  const urlBucket: LeadBucket | null =
    urlBucketRaw === 'OPEN' || urlBucketRaw === 'WON' || urlBucketRaw === 'LOST'
      ? urlBucketRaw
      : null;
  const urlStage = searchParams.get('stage'); // forwarded from tracker tile (status uuid)
  const urlRequiresFirstContact = searchParams.get('requiresFirstContact') === 'true';
  const urlRevertPending = searchParams.get('revertPending') === 'true';
  const urlLargeLeadQuery = searchParams.get('largeLeadQuery') === 'true';
  const urlNextFollowUpFrom = searchParams.get('nextFollowUpFrom');
  const urlNextFollowUpTo = searchParams.get('nextFollowUpTo');
  const urlCarryforwardOnly =
    searchParams.get('f.carryforward') === 'yes' || searchParams.get('carryforward') === 'yes';

  // Carry-forward fold-in (`includeActiveCarryover`). Opt in whenever
  // the host asked for it AND a date window exists for the server to
  // anchor against. Calendar-month CF (strict pre-month OPEN slice /
  // the Carry Forward strip) is already independent of the inbox
  // range; this flag is the quieter "keep still-OPEN leads on screen
  // as the window moves" behaviour on Pipeline / All-leads.
  //
  // Not gated on the date-range *preset* (this_month / last_7_days /
  // custom / …) - any bounded window is enough. `daterange=all` still
  // skips it: there is no lower bound to OR against, and every live
  // lead is already in the result set.
  //
  // Also suppressed when the current chips / deep-links would make
  // the fold-in contradictory (non-OPEN bucket, a specific stage,
  // Revert Pending / Today's Follow-up / etc.). Mirrors the
  // LeadInboxAside auto-suppression in ADR-0042 §5.
  const activeCarryoverEligible =
    enableActiveCarryover === true &&
    includeDateRange &&
    !urlCarryforwardOnly &&
    !urlBucket &&
    !urlStage &&
    !urlRequiresFirstContact &&
    !urlRevertPending &&
    !urlLargeLeadQuery &&
    !urlNextFollowUpFrom &&
    !urlNextFollowUpTo &&
    !urlExitUnassigned;
  // (Earlier this file derived a `carryoverLowerBoundMs` here to gate
  // the row-level "Carry forward" pill. That bound was the inbox's
  // `createdAtFrom` - which meant the pill flickered on/off as the
  // operator changed the date range. The pill is now driven by the
  // canonical `isCarryforwardLead` helper - same rule the Carry
  // Forward strip uses - so this memo is gone. `activeCarryoverEligible`
  // is still used to opt the underlying list query into the
  // `includeActiveCarryover` flag.)

  // Live status options for the new "Stage" filter. Same hook the inbox
  // aside uses, so the cache is shared and the dropdown stays in lockstep
  // with the chips on the detail screen.
  const statuses = useQueryStatusOptionsWithBucket();
  // Agents drive the admin-only "Assigned To" dropdown. Scoped roles
  // (Sales Lead etc.) already see only their own / their team's rows via
  // server-side visibility scoping, so an admin-exclusive client filter
  // is enough - we don't need to enforce it server-side.
  const me = useCurrentUser();
  const isAdmin = me?.role === 'ADMIN';
  const isSalesLead = me?.role === 'SALES_LEAD';
  const canManageExitUnassigned = isAdmin || isSalesLead;
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignDialogRows, setAssignDialogRows] = useState<LeadListItem[]>([]);
  // The All-leads counterpart of the exit queue's assign: filter by
  // Destination / Assigned To, tick the rows, hand them to one agent.
  const [reassignRows, setReassignRows] = useState<LeadListItem[] | null>(null);
  const agents = useAgentOptionsForLeads();
  // Destinations drive the "Destination" filter chip (all grids, incl. the
  // Unassigned queue - useful for reassigning a destination's released leads).
  const destinations = useDestinationOptionsForLeads();
  // Only fetched on the exit-unassigned page, for its "Former owner" chip.
  const formerOwners = useExitUnassignedFormerOwners(urlExitUnassigned && canManageExitUnassigned);
  // Resolve the locked stage code → id once the statuses cache lands.
  // Memoised on the code + data so the toQuery callback below sees a
  // stable id and doesn't churn the underlying React Query.
  const forcedStatusId = useMemo(
    () =>
      forcedStatusCode ? statuses.data.find((s) => s.code === forcedStatusCode)?.id : undefined,
    [forcedStatusCode, statuses.data],
  );
  // Same resolution for the exclude side. Wired separately so a tab
  // can use ONE of {forced, exclude} or neither - the Pipeline tab
  // excludes `long_followup`, the Long Follow-up tab forces it, the
  // All-leads tab excludes it (dedicated Long Follow-up tab only).
  const excludeStatusId = useMemo(
    () =>
      excludeStatusCode ? statuses.data.find((s) => s.code === excludeStatusCode)?.id : undefined,
    [excludeStatusCode, statuses.data],
  );
  const filters: ReadonlyArray<DataGridFilter> = useMemo(() => {
    // Drop the excluded status from the dropdown so the operator
    // can't pick a value that would always return zero rows. The
    // exclude is still applied server-side regardless.
    const visibleStatuses = excludeStatusCode
      ? statuses.data.filter((s) => s.code !== excludeStatusCode)
      : statuses.data;
    const statusOptions: ReadonlyArray<{ value: string; label: string }> = [
      { value: '', label: 'All stages' },
      ...visibleStatuses.map((s) => ({ value: s.id, label: s.name })),
    ];
    const assignedOptions: ReadonlyArray<{ value: string; label: string }> = [
      { value: '', label: 'All assignees' },
      // Sentinel (not a real agent UUID) - the toQuery builder maps it to
      // `assignedAgentIsNull` so the grid lists only no-agent leads.
      { value: UNASSIGNED_ASSIGNEE_FILTER, label: 'Unassigned' },
      ...agents.options,
    ];
    // "Assigned To" is meaningless in the exit-unassigned queue - every row has
    // no agent by definition - so it's dropped there; every other chip (Created
    // month / Priority / Stage / Budget) mirrors the All-leads grid and is
    // honoured server-side (see the exitQueueMode filter block in leads.service).
    const adminFilters: DataGridFilter[] =
      canManageExitUnassigned && !urlExitUnassigned
        ? [
            {
              id: 'assignedAgentId',
              label: 'Assigned To',
              options: assignedOptions,
              widthClass: 'w-52',
            },
          ]
        : [];
    // Its counterpart in the exit queue: who the leads were released FROM.
    // Options come from the queue itself, since departed users are in no
    // agent picker; the count beside each name says how many rows it leaves.
    const formerOwnerFilter: DataGridFilter[] =
      urlExitUnassigned && canManageExitUnassigned
        ? [
            {
              id: 'formerOwner',
              label: 'Former owner',
              options: [
                { value: '', label: 'All former owners' },
                ...(formerOwners.data?.data ?? []).map((owner) => ({
                  value: owner.id,
                  label: `${owner.fullName} (${owner.leadCount})`,
                })),
              ],
              widthClass: 'w-56',
            },
          ]
        : [];
    // When the stage is locked by the host tab, drop the Stage chip
    // entirely - showing it as a disabled dropdown would invite the
    // operator to wonder why it's stuck.
    const stageFilter: DataGridFilter[] = forcedStatusCode
      ? []
      : [{ id: 'status', label: 'Stage', options: statusOptions, widthClass: 'w-44' }];
    const destinationFilter: DataGridFilter = {
      id: 'destinationId',
      label: 'Destination',
      options: [{ value: '', label: 'All destinations' }, ...destinations.options],
      widthClass: 'w-56',
    };
    return [
      ...FILTERS_STATIC,
      ...stageFilter,
      ...adminFilters,
      ...formerOwnerFilter,
      destinationFilter,
      { id: 'budget', label: 'Budget', options: BUDGET_OPTIONS, widthClass: 'w-44' },
    ];
  }, [
    statuses.data,
    agents.options,
    destinations.options,
    formerOwners.data,
    canManageExitUnassigned,
    isAdmin,
    forcedStatusCode,
    excludeStatusCode,
    urlExitUnassigned,
  ]);

  const columns = useLeadListColumns({ isAdmin });

  // Grid → detail deep-link. Carry the SAME filter context the grid is
  // showing into the detail URL so the aside scopes to the identical
  // slice. Without this the aside got only `ownerId` and fell back to its
  // active-this-month lens - so a "Today" grid opened a detail whose chip
  // counts read the whole month (the reported mismatch). Mirrors the
  // params the tracker tiles already forward (from/to/daterange + any
  // bucket / stage / tile drill-in).
  const buildDetailSearch = (): string => {
    const p = new URLSearchParams();
    // Only forward a date window when the URL carries an explicit one (a
    // tile drill-in or a picked range). A bare default view forwards
    // nothing, leaving the aside's own lens as the default there. Uses the
    // resolved bounds so a preset-only link (`daterange=today`, no from/to)
    // still hands the aside concrete bounds.
    const hasDateContext = Boolean(
      searchParams.get('from') || searchParams.get('to') || searchParams.get('daterange'),
    );
    if (hasDateContext) {
      if (effectivePreset) p.set('daterange', effectivePreset);
      if (includeDateRange && createdAtFrom) p.set('from', createdAtFrom);
      if (includeDateRange && createdAtTo) p.set('to', createdAtTo);
    }
    if (urlBucket) p.set('bucket', urlBucket);
    if (urlStage) p.set('stage', urlStage);
    if (urlRequiresFirstContact) p.set('requiresFirstContact', 'true');
    if (urlRevertPending) p.set('revertPending', 'true');
    if (urlLargeLeadQuery) p.set('largeLeadQuery', 'true');
    if (urlNextFollowUpFrom) p.set('nextFollowUpFrom', urlNextFollowUpFrom);
    if (urlNextFollowUpTo) p.set('nextFollowUpTo', urlNextFollowUpTo);
    if (urlCarryforwardOnly) p.set('f.carryforward', 'yes');
    if (ownerId) p.set('ownerId', ownerId);
    return p.toString();
  };

  return (
    <>
      {urlExitUnassigned && canManageExitUnassigned && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm text-amber-950">
          <span>
            Showing leads released from departed agents. Select rows and assign them to an active
            agent.
          </span>
        </div>
      )}
      <ListScreen<LeadListItem, LeadListQuery>
        embedded={embedded}
        // Leads carry highly variable content (customer names, references,
        // destinations); fixed-width truncation hides too much. Auto layout
        // gives each column the room its widest cell needs.
        tableLayout="auto"
        title="Lead inbox"
        description="Every enquiry from CRM channels. Website form leads appear only on Lead Parsing."
        useList={useLeadsList}
        defaultSort={{ by: 'createdAt', dir: 'desc' }}
        minSearchLength={MIN_SEARCH_LENGTH}
        createButton={{ label: 'New lead', onClick: () => navigate('/leads/new') }}
        toQuery={(state, base) => {
          const priority = state.filters.priority as '' | LeadPriority;
          const due = state.filters.due;
          const carryforwardOnly = state.filters.carryforward === 'yes' || urlCarryforwardOnly;
          // "Without carry forward": suppress the carryover fold-in so the
          // list stays bounded to the selected date window and older
          // still-active leads don't roll in.
          const carryforwardExclude = state.filters.carryforward === 'no';
          const dropdownStage = state.filters.status as string | undefined;
          // Resolution order when the stage is locked by the host tab
          // (Long Follow-up): forcedStatusId wins over everything. For
          // unlocked tabs we keep the original "URL beats dropdown"
          // rule used by the tracker deep-link.
          const stage = forcedStatusId ?? (urlStage || dropdownStage || undefined);
          // The server takes `q` (trigram fuzzy match across reference, title,
          // customer name) - not the generic `search` base field.
          const q = base.search?.trim() ? base.search : undefined;
          // Admin-only assignee filter. The chip is hidden for non-admins,
          // but a stale URL param could still arrive here - server-side
          // visibility scoping enforces the role boundary regardless.
          // 2026-07-08. If the inbox's role-hierarchy owner picker set an
          // `ownerId` prop, it wins over the internal chip. Non-admin
          // callers may still pass a valid `ownerId` (a SALES_LEAD scoping
          // to one of their reports), and the server enforces they can
          // only pick within their own scope.
          const rawAssignedFilter = isAdmin
            ? (state.filters.assignedAgentId as string | undefined)
            : undefined;
          // "Unassigned" sentinel -> filter the grid to no-agent leads via the
          // server `assignedAgentIsNull` flag. The role-hierarchy `ownerId` (if
          // set) wins, so this only applies when no specific owner is scoped.
          const wantsUnassigned = rawAssignedFilter === UNASSIGNED_ASSIGNEE_FILTER && !ownerId;
          const chipAgentId = wantsUnassigned ? undefined : rawAssignedFilter;
          const assignedAgentId = ownerId ?? chipAgentId;
          const assignmentFilter: Partial<LeadListQuery> = wantsUnassigned
            ? { assignedAgentIsNull: true }
            : assignedAgentId
              ? { assignedAgentId }
              : {};
          // Budget bracket -> numeric range.
          const budgetRange = parseBudgetBracket(state.filters.budget as string | undefined);
          // Destination filter chip -> server `destinationId`.
          const destinationId = (state.filters.destinationId as string | undefined) || undefined;
          const destinationScope: Partial<LeadListQuery> = destinationId ? { destinationId } : {};
          // "Created month" filter: when set it pins the createdAt window to
          // that calendar month, overriding the header date range (and it works
          // in the exit-unassigned view, which otherwise ignores dates).
          const monthWindow = monthBounds(state.filters.createdMonth as string | undefined);
          const createdWindow: Partial<LeadListQuery> = monthWindow
            ? { createdAtFrom: monthWindow.from, createdAtTo: monthWindow.to }
            : {
                ...(includeDateRange && createdAtFrom ? { createdAtFrom } : {}),
                ...(includeDateRange && createdAtTo ? { createdAtTo } : {}),
              };
          const { search: _ignored, ...rest } = base;
          // Strict carry-forward slice: OPEN-bucket leads created before
          // this month. Matches the Carry Forward tracker strip and the
          // row-level pill rule in `isCarryforwardLead`.
          if (carryforwardOnly) {
            return {
              ...rest,
              ...(q ? { q } : {}),
              excludeWebsite: true,
              // Fold the unassigned (exit-released) pool into the grid so its
              // Total matches the Daily Tracker / KPI tiles, which count with
              // includeUnassignedPool. Non-admins are still fenced to their own
              // agents by RBAC scope, so this only surfaces rows for unscoped
              // (ADMIN/OPS) roles - the same roles the tiles include them for.
              includeUnassignedPool: true,
              currentBucket: 'OPEN',
              // Floored at the cutover anchor - the legacy import carries OPEN
              // leads back to 2021 which are not real carry-forward pipeline.
              createdAtFrom: CARRYFORWARD_FLOOR_ISO,
              createdAtTo: getPrevMonthEndIso(),
              ...(priority ? { priority } : {}),
              ...(stage ? { currentStatusId: stage } : {}),
              ...(excludeStatusId ? { excludeStatusId } : {}),
              ...(due === 'due' ? { followUpDue: true } : {}),
              ...(urlRequiresFirstContact ? { requiresFirstContact: true } : {}),
              ...(urlRevertPending ? { revertPending: true } : {}),
              ...(urlLargeLeadQuery ? { largeLeadQuery: true } : {}),
              ...(urlNextFollowUpFrom ? { nextFollowUpFrom: urlNextFollowUpFrom } : {}),
              ...(urlNextFollowUpTo ? { nextFollowUpTo: urlNextFollowUpTo } : {}),
              ...assignmentFilter,
              ...(budgetRange?.from !== undefined ? { budgetFrom: budgetRange.from } : {}),
              ...(budgetRange?.to !== undefined ? { budgetTo: budgetRange.to } : {}),
              ...destinationScope,
            };
          }
          if (urlExitUnassigned && canManageExitUnassigned) {
            return {
              ...rest,
              ...(q ? { q } : {}),
              exitUnassigned: true,
              excludeWebsite: true,
              // Unassigned = EVERY no-agent lead, date-independent by design. The
              // header range must NOT narrow it (a this_month default would hide
              // every lead not created this month - on the 1st, all of them). Only
              // an EXPLICIT "created month" filter picked on the page narrows it.
              ...(monthWindow
                ? { createdAtFrom: monthWindow.from, createdAtTo: monthWindow.to }
                : {}),
              // Same filter chips as the All-leads grid (server honours these in
              // exitQueueMode). Assigned-agent is intentionally absent - the queue
              // is unassigned by definition; "Former owner" stands in for it.
              ...(state.filters.formerOwner
                ? { unassignedFromAgentId: state.filters.formerOwner as string }
                : {}),
              ...(priority ? { priority } : {}),
              ...(stage ? { currentStatusId: stage } : {}),
              ...(budgetRange?.from !== undefined ? { budgetFrom: budgetRange.from } : {}),
              ...(budgetRange?.to !== undefined ? { budgetTo: budgetRange.to } : {}),
              ...destinationScope,
            };
          }
          return {
            ...rest,
            ...(q ? { q } : {}),
            excludeWebsite: true,
            // Fold the unassigned (exit-released) pool into the grid so its Total
            // matches the tiles (which count with includeUnassignedPool). RBAC
            // scope still fences non-admins to their own agents, so this only
            // surfaces unassigned rows for unscoped (ADMIN/OPS) roles.
            includeUnassignedPool: true,
            ...(urlBucket ? { currentBucket: urlBucket } : {}),
            ...(priority ? { priority } : {}),
            ...(stage ? { currentStatusId: stage } : {}),
            // Host-tab exclusion (e.g. Pipeline hiding `long_followup`).
            // Composes with the include side server-side - if the
            // operator picks a stage that equals the excluded one,
            // they'd get zero rows, but we already drop that stage
            // from the dropdown so it's not reachable from the UI.
            ...(excludeStatusId ? { excludeStatusId } : {}),
            ...(due === 'due' ? { followUpDue: true } : {}),
            ...(urlRequiresFirstContact ? { requiresFirstContact: true } : {}),
            ...(urlRevertPending ? { revertPending: true } : {}),
            ...(urlLargeLeadQuery ? { largeLeadQuery: true } : {}),
            ...(urlNextFollowUpFrom ? { nextFollowUpFrom: urlNextFollowUpFrom } : {}),
            ...(urlNextFollowUpTo ? { nextFollowUpTo: urlNextFollowUpTo } : {}),
            ...createdWindow,
            // Carry-forward fold-in: still-OPEN leads created before the
            // selected window ride along with the in-window rows, floored
            // at the cutover anchor so the legacy import's pre-2021 OPEN
            // backlog stays out. Same window the Daily Tracker's "Carry
            // fwd" tile counts, so grid and tile agree. Suppressed when a
            // "Created month" is picked - that filter means "created IN this
            // month", so folding in older still-open leads would break it.
            ...(activeCarryoverEligible && !carryforwardExclude && !monthWindow
              ? {
                  includeActiveCarryover: true,
                  activeCarryoverFrom: CARRYFORWARD_FLOOR_ISO,
                  // Also fold in this-period closures (Won/Lost) so the grid
                  // Total matches the tile's "open + closed this period" (a July
                  // lead lost in August still shows). No bucket/stage filter is
                  // active here (activeCarryoverEligible requires that), so the
                  // closures leg genuinely adds rows rather than being ANDed out.
                  includeClosedThisPeriod: true,
                }
              : {}),
            ...assignmentFilter,
            ...(budgetRange?.from !== undefined ? { budgetFrom: budgetRange.from } : {}),
            ...(budgetRange?.to !== undefined ? { budgetTo: budgetRange.to } : {}),
            ...destinationScope,
          };
        }}
        columns={columns}
        filters={filters}
        getRowId={(row) => row.id}
        // Excel export - pages through every row for the CURRENT filters
        // (date range, owner, stage, priority, budget, carry-forward, search)
        // via fetchAllPages, so the file matches exactly what the grid shows.
        // Admin-only: the bulk lead export is a supervisor capability, so
        // non-admins (incl. SALES_LEAD on the exit-unassigned queue) don't
        // get the Export button at all.
        export={
          isAdmin
            ? {
                fetchPage: (q) => listLeads(q),
                filename: 'leads',
                csv: leadExportColumns,
                // The date range lives in the page header (URL), not the grid's
                // own filters - surface it in the export dialog so the operator
                // sees the window they're exporting.
                contextRows: [
                  {
                    label: 'Date range',
                    value: !includeDateRange
                      ? 'All time'
                      : createdAtFrom && createdAtTo
                        ? `${createdAtFrom} → ${createdAtTo}`
                        : 'This month',
                  },
                ],
              }
            : undefined
        }
        // 2026-07-16. Carry the inbox's owner scope into the detail page
        // so the aside's OwnerFilter opens pre-selected to the same
        // owner this grid was filtered by.
        onRowClick={(row) => {
          const search = buildDetailSearch();
          navigate(`/leads/${row.id}${search ? `?${search}` : ''}`);
        }}
        copy={{
          searchPlaceholder: 'Search reference, customer, phone, email (min 3 characters)',
          emptyTitle: urlExitUnassigned
            ? 'No unassigned leads in your queue'
            : 'No leads match these filters',
          emptyDescription: urlExitUnassigned
            ? 'Leads appear here when a sales agent is deactivated or removed and their active / not qualified leads are released.'
            : 'Adjust the bucket, priority, or date filter, or clear the search box.',
          errorTitle: 'Failed to load leads',
          errorDescription: 'The server returned an error. Try refreshing or check your network.',
        }}
        bulkActions={
          urlExitUnassigned && canManageExitUnassigned
            ? {
                extra: [
                  {
                    id: 'assign-exit-unassigned',
                    label: 'Assign to agent',
                    onAction: (_ids, rows) => {
                      setAssignDialogRows([...rows]);
                      setAssignDialogOpen(true);
                    },
                  },
                ],
              }
            : canManageExitUnassigned
              ? {
                  extra: [
                    {
                      id: 'reassign-leads',
                      label: 'Reassign to agent',
                      onAction: (_ids, rows) => setReassignRows([...rows]),
                    },
                  ],
                }
              : undefined
        }
      />
      <ExitUnassignedAssignDialog
        open={assignDialogOpen}
        rows={assignDialogRows}
        onOpenChange={setAssignDialogOpen}
      />
      <LeadBulkReassignDialog
        leads={reassignRows}
        onClose={() => setReassignRows(null)}
        onDone={() => setReassignRows(null)}
      />
    </>
  );
};
